/**
 * La carta. Se usa igual en la mano, en el tablero y (más adelante) en la
 * colección y el mercado, cambiando solo `variant`.
 *
 * Tres decisiones de diseño que condicionan el resto de la interfaz:
 *
 * - **El poder actual manda sobre el impreso.** Si un efecto la ha modificado,
 *   se pinta el número real y se colorea según suba o baje. En un juego donde
 *   todo son buffs y debuffs, el número impreso es la información menos útil.
 * - **Nada de texto ilegible.** En el tablero caben 8 cartas por zona en un
 *   móvil: ahí solo se pintan coste, poder e icono. El texto aparece en la mano,
 *   donde hay sitio.
 * - **El reverso no filtra nada.** Una carta boca abajo del rival no muestra ni
 *   coste ni poder; si no, la fase de planificación simultánea sería un farol
 *   transparente.
 */

import type { CardInstance, CardMaster } from '@/types/game';
import { getCardMaster } from '@/data';
import { basePowerOf } from '@/engine/effects';
import { RARITY_STYLES, artUrlFor, iconFor } from './cardArt';

export type CardVariant = 'board' | 'hand';

interface CardViewProps {
  card: CardInstance;
  variant?: CardVariant;
  /** Poder efectivo con modificadores. Si falta, se usa el poder base. */
  power?: number;
  faceDown?: boolean;
  /** Se puede pagar y jugar ahora mismo. Las que no, se atenúan. */
  playable?: boolean;
  selected?: boolean;
  /** Se está revelando justo ahora: destaca sobre las demás. */
  highlighted?: boolean;
  className?: string;
}

function CostBadge({ cost }: { cost: number }) {
  return (
    <span className="absolute -top-1.5 -left-1.5 z-10 grid h-5 w-5 place-items-center rounded-full border border-sky-200/70 bg-sky-600 text-[10px] font-bold text-white shadow-md sm:h-6 sm:w-6 sm:text-xs">
      {cost}
    </span>
  );
}

function PowerBadge({ power, base }: { power: number; base: number }) {
  const tone =
    power > base
      ? 'bg-emerald-500 border-emerald-200/70'
      : power < base
        ? 'bg-rose-600 border-rose-200/70'
        : 'bg-amber-500 border-amber-200/70';
  return (
    <span
      className={`absolute -top-1.5 -right-1.5 z-10 grid h-5 w-5 place-items-center rounded-full border text-[10px] font-bold text-white shadow-md sm:h-6 sm:w-6 sm:text-xs ${tone}`}
    >
      {power}
    </span>
  );
}

/** Reverso: mismo tamaño y forma, cero información. */
function CardBack({ className = '' }: { className?: string }) {
  return (
    <div
      className={`relative aspect-5/7 w-full overflow-hidden rounded-lg border-2 border-indigo-300/40 bg-linear-to-br from-indigo-800 to-slate-950 shadow-lg ${className}`}
    >
      <div className="absolute inset-0 opacity-40 [background-image:repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(165,180,252,0.35)_4px,rgba(165,180,252,0.35)_5px)]" />
      <div className="absolute inset-0 grid place-items-center">
        <div className="h-1/3 w-1/3 rotate-45 rounded-sm border-2 border-indigo-200/70" />
      </div>
    </div>
  );
}

export function CardView({
  card,
  variant = 'board',
  power,
  faceDown = false,
  playable = true,
  selected = false,
  highlighted = false,
  className = '',
}: CardViewProps) {
  if (faceDown) return <CardBack className={className} />;

  const master: CardMaster = getCardMaster(card.masterId);
  const base = basePowerOf(card);
  const effectivePower = power ?? base;
  const rarity = RARITY_STYLES[master.rarity];
  const Icon = iconFor(master.art.icon);
  const artUrl = artUrlFor(master);
  const isHand = variant === 'hand';

  return (
    <div
      className={[
        'relative aspect-5/7 w-full overflow-hidden rounded-lg border-2 shadow-lg transition',
        'bg-linear-to-br',
        master.art.gradient,
        rarity.border,
        highlighted ? 'ring-2 ring-white ring-offset-1 ring-offset-slate-900' : '',
        selected ? 'ring-2 ring-amber-300' : '',
        playable ? rarity.glow : 'opacity-55 saturate-50',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <CostBadge cost={master.cost} />
      <PowerBadge power={effectivePower} base={base} />

      {/* Ilustración. El icono solo aparece si la carta no tiene ninguna imagen.
          alt vacío a propósito: es decoración, y el nombre ya va en texto justo
          debajo — un lector de pantalla que leyera los dos diría lo mismo dos
          veces. */}
      {artUrl ? (
        <img
          src={artUrl}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 grid place-items-center">
          <Icon
            className="h-1/2 w-1/2 text-white/85 drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]"
            strokeWidth={1.5}
            aria-hidden
          />
        </div>
      )}

      {/* Franja inferior. El degradado garantiza contraste sobre cualquier arte. */}
      <div className="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/85 to-transparent px-1 pt-3 pb-1">
        <p
          className={`truncate text-center text-[8px] leading-tight font-semibold sm:text-[10px] ${rarity.text}`}
        >
          {master.name}
        </p>
        {isHand && master.effectDescription ? (
          <p className="mt-0.5 line-clamp-3 text-center text-[7px] leading-tight text-white/75 sm:text-[9px]">
            {master.effectDescription}
          </p>
        ) : null}
      </div>

      {/* Nivel de fusión. Solo aparece cuando hay algo que contar. */}
      {card.level > 1 ? (
        <span className="absolute right-0.5 bottom-0.5 z-10 rounded-sm bg-amber-400/90 px-1 text-[7px] font-bold text-amber-950 sm:text-[9px]">
          N{card.level}
        </span>
      ) : null}
    </div>
  );
}
